import { useEffect } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SidebarProvider } from "@/contexts/SidebarContext";
import { UserRole } from "@/lib/schema";
import Landing from "@/pages/Landing";
import AvailableJobs from "@/pages/AvailableJobs";
import JobDetails from "@/pages/JobDetails";
import Dashboard from "@/pages/Dashboard";
import Referrals from "@/pages/Referrals";
import Rewards from "@/pages/Rewards";
import ClientReferral from "@/pages/ClientReferral";
import SuperAdmin from "@/pages/admin/SuperAdmin";
import JobHub from "@/pages/admin/JobHub";
import AdminJobDetails from "@/pages/admin/JobDetails";
import ReferralManagement from "@/pages/admin/ReferralManagement";
import CreateJob from "@/pages/admin/CreateJob";
import UserManagement from "@/pages/admin/UserManagement";
import JobSettings from "@/pages/admin/settings/JobSettings";
import ReferralSettings from "@/pages/admin/settings/ReferralSettings";
import RewardSettings from "@/pages/admin/settings/RewardSettings";
import GraphRAGTuning from "@/pages/admin/settings/GraphRAGTuning";
import AskAI from "@/pages/admin/AskAI";
import ClientReview from "@/pages/admin/ClientReview";
import RewardsHub from "@/pages/admin/RewardsHub";
import CVMissing from "@/pages/admin/CVMissing";
import PendingProbation from "@/pages/admin/PendingProbation";
import NotFound from "@/pages/not-found";
import ResetPassword from "@/pages/ResetPassword";
import { CompleteProfile } from "@/pages/CompleteProfile";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import { ScrollToTop } from "@/components/ui/ScrollToTop";
import * as Sentry from "@sentry/react";

// Initialize Sentry for error tracking - only if DSN is provided
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring - Optimized for cost and performance
    tracesSampleRate: 0.2,  // 20% sampling to reduce costs
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // IMPORTANT: Exclude ALL Firebase services to avoid CORS issues with Sentry's baggage headers
    // Firebase SDK handles its own request management and doesn't need distributed tracing
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/airtap\.co\.za/,  // Only trace our own domain
      // Firebase services are intentionally excluded:
      // - Firebase Functions (cloudfunctions.net) - CORS issues with baggage headers
      // - Firebase Storage (firebasestorage.app) - CORS issues with custom headers
      // - Firebase Firestore - Uses WebSockets, incompatible with HTTP tracing
    ],
    // Session Replay - Optimized sampling
    replaysSessionSampleRate: 0.1, // Sample 10% of sessions
    replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions with errors
    // Environment configuration
    environment: import.meta.env.MODE || "development",
    // Setting this option to true will send default PII data to Sentry
    sendDefaultPii: true,
    // Only enable in production
    enabled: import.meta.env.PROD,
    // Filter out known IndexedDB errors that are browser-level issues
    beforeSend(event, hint) {
      const error = hint.originalException;
      
      // Filter IndexedDB connection errors (common on iOS Safari)
      if (error && typeof error === 'object') {
        const errorObj = error as any;
        const errorMessage = errorObj.message || '';
        const errorName = errorObj.name || '';
        
        // IndexedDB connection errors (not actionable, browser-level issue)
        if (
          (errorName === 'UnknownError' && errorMessage.includes('IndexedDB')) ||
          (errorName === 'UnknownError' && errorMessage.includes('Indexed Database')) ||
          (errorObj.code === 0 && errorMessage.includes('Connection'))
        ) {
          console.warn('Filtered IndexedDB error from Sentry:', errorMessage);
          return null; // Don't send to Sentry
        }
      }
      
      return event;
    },
  });
}

function Router() {
  const { isNewGoogleUser, loading, userData } = useAuth();
  const [location, navigate] = useLocation();

  const needsProfileCompletion =
    !loading && 
    userData && 
    userData.role === UserRole.USER && 
    (isNewGoogleUser || !userData.onboardingCompleted);

  useEffect(() => {
    if (needsProfileCompletion && location !== "/complete-profile") {
      navigate("/complete-profile", { replace: true });
    }
  }, [needsProfileCompletion, location, navigate]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (needsProfileCompletion) {
    return (
      <Switch>
        <Route path="/complete-profile" component={CompleteProfile} />
        <Route>
          <LoadingScreen />
        </Route>
      </Switch>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Switch>
        <Route path="/" component={Landing} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/jobs" component={AvailableJobs} />
        <Route path="/jobs/:id" component={JobDetails} />
        <Route path="/dashboard" component={Dashboard} />
        <Route path="/referrals" component={Referrals} />
        <Route path="/rewards" component={Rewards} />
        <Route path="/client-referral" component={ClientReferral} />
        <Route path="/admin" component={SuperAdmin} />
        <Route path="/admin/users" component={UserManagement} />
        <Route path="/admin/settings/jobs" component={JobSettings} />
        <Route path="/admin/settings/referrals" component={ReferralSettings} />
        <Route path="/admin/settings/rewards" component={RewardSettings} />
        <Route path="/admin/settings/graphrag" component={GraphRAGTuning} />
        <Route path="/admin/ai/chat" component={AskAI} />
        <Route path="/admin/client-referral" component={ClientReview} />
        <Route path="/admin/jobs" component={JobHub} />
        <Route path="/admin/jobs/create" component={CreateJob} />
        <Route path="/admin/jobs/:id" component={AdminJobDetails} />
        <Route path="/admin/referrals" component={ReferralManagement} />
        <Route path="/admin/cv-missing" component={CVMissing} />
        <Route path="/admin/pending-probation" component={PendingProbation} />
        <Route path="/admin/rewards-hub" component={RewardsHub} />
        <Route component={NotFound} />
      </Switch>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SidebarProvider>
          <TooltipProvider>
            <Toaster />
            <Router />
          </TooltipProvider>
        </SidebarProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
